import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  getAdmissionEntries,
  getContactEntries,
  exportAdmissionsToExcel,
  exportContactToExcel,
  StringRecord,
} from '@/lib/exportToExcel';
import { Download, Users, MessageSquare, Trash2, RefreshCw } from 'lucide-react';

export default function Admin() {
  const [admissions, setAdmissions] = useState<StringRecord[]>([]);
  const [contacts, setContacts] = useState<StringRecord[]>([]);
  const [activeTab, setActiveTab] = useState<'admissions' | 'contacts'>('admissions');

  const loadData = () => {
    setAdmissions(getAdmissionEntries());
    setContacts(getContactEntries());
  };

  useEffect(() => {
    loadData();
  }, []);

  const clearAdmissions = () => {
    if (confirm('هل أنت متأكد من حذف جميع بيانات طلبات التسجيل؟')) {
      localStorage.removeItem('cgot_admissions_data');
      setAdmissions([]);
    }
  };

  const clearContacts = () => {
    if (confirm('هل أنت متأكد من حذف جميع رسائل التواصل؟')) {
      localStorage.removeItem('cgot_contact_data');
      setContacts([]);
    }
  };

  const currentData = activeTab === 'admissions' ? admissions : contacts;
  const headers = currentData.length > 0 ? Object.keys(currentData[0]) : [];

  return (
    <Layout>
      <div className="min-h-screen bg-muted/30 py-10" dir="rtl">
        <div className="container mx-auto px-4">

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">لوحة إدارة البيانات</h1>
            <p className="text-muted-foreground">عرض وتصدير بيانات نماذج الموقع إلى ملف Excel</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Card
              className={`p-6 cursor-pointer border-2 transition-all ${activeTab === 'admissions' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
              onClick={() => setActiveTab('admissions')}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{admissions.length}</p>
                  <p className="text-sm text-muted-foreground">طلب تسجيل</p>
                </div>
              </div>
            </Card>

            <Card
              className={`p-6 cursor-pointer border-2 transition-all ${activeTab === 'contacts' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
              onClick={() => setActiveTab('contacts')}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{contacts.length}</p>
                  <p className="text-sm text-muted-foreground">رسالة تواصل</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Button
              onClick={() => activeTab === 'admissions' ? exportAdmissionsToExcel() : exportContactToExcel()}
              className="flex items-center gap-2"
              disabled={currentData.length === 0}
            >
              <Download className="w-4 h-4" />
              تحميل {activeTab === 'admissions' ? 'طلبات التسجيل' : 'رسائل التواصل'} (Excel)
            </Button>

            <Button variant="outline" onClick={loadData} className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              تحديث
            </Button>

            <Button
              variant="destructive"
              onClick={() => activeTab === 'admissions' ? clearAdmissions() : clearContacts()}
              className="flex items-center gap-2"
              disabled={currentData.length === 0}
            >
              <Trash2 className="w-4 h-4" />
              حذف الكل
            </Button>
          </div>

          {/* Table */}
          <Card className="overflow-hidden">
            {currentData.length === 0 ? (
              <div className="p-16 text-center">
                <div className="w-16 h-16 rounded-full bg-muted mx-auto flex items-center justify-center mb-4">
                  {activeTab === 'admissions'
                    ? <Users className="w-8 h-8 text-muted-foreground" />
                    : <MessageSquare className="w-8 h-8 text-muted-foreground" />
                  }
                </div>
                <p className="text-lg font-medium text-muted-foreground">
                  {activeTab === 'admissions' ? 'لا توجد طلبات تسجيل بعد' : 'لا توجد رسائل بعد'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">ستظهر البيانات هنا بعد تعبئة النماذج</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-primary text-primary-foreground">
                    <tr>
                      <th className="px-4 py-3 text-right font-medium">#</th>
                      {headers.map((h) => (
                        <th key={h} className="px-4 py-3 text-right font-medium whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                        <td className="px-4 py-3 text-muted-foreground">{i + 1}</td>
                        {headers.map((h) => (
                          <td key={h} className="px-4 py-3 whitespace-nowrap">{row[h] ?? '-'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Note */}
          <p className="mt-4 text-xs text-muted-foreground text-center">
            ⚠️ البيانات محفوظة في المتصفح (localStorage) — يُنصح بتصديرها بانتظام
          </p>
        </div>
      </div>
    </Layout>
  );
}
